import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Avatar,
  CircularProgress,
  IconButton,
  Divider,
  useTheme,
  alpha,
  Tabs,
  Tab,
  Chip,
  Alert,
  Snackbar,
  Button,
} from '@mui/material';
import React, { useState, useRef, useEffect, useContext } from 'react';

import EnhancedMessage from '@/components/ai-assistant/EnhancedMessage';
import { TenantContext } from '../../contexts/TenantContext';
import { useVoice } from '../../contexts/VoiceContext';
import { omniscientAgentService, OmniscientAgentResponse, ActionButton } from '../../services/ai/omniscientAgentService';
import { sendMessageToEnhancedAI, processVoiceCommand } from '../../services/aiAssistant';
import { ChatMessage, ChatResponse } from '../../types/ApiTypes';
import { getAuthToken, getUserInfo } from '../../utils/auth';

// Extended message interface for enhanced AI features
interface ExtendedChatMessage {
  id: string;
  timestamp: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  metadata?: {
    agents_used?: string[];
    confidence_score?: number;
    execution_time?: number;
    workflow_type?: string;
    enterprise_features?: {
      multi_agent_used: boolean;
      workflow_orchestration: boolean;
      legal_compliance_checked: boolean;
      audit_trail_created: boolean;
    };
    voice_processed?: boolean;
    workflow_executed?: boolean;
  };
  actions?: Array<{
    label: string;
    action: string;
    data?: any;
  }>;
  error?: boolean;
}

// Custom prompt categories
const customPrompts = [
  {
    category: 'Business Documents',
    prompts: [
      {
        name: 'Contract Drafting',
        prompt: 'Help me draft a business contract with standard terms and conditions.',
      },
      {
        name: 'NDA Review',
        prompt: 'Review this NDA for potential risks and suggest improvements.',
      },
      {
        name: 'Terms of Service',
        prompt: 'Help me create comprehensive terms of service for my website.',
      },
    ],
  },
  {
    category: 'Employment Laws',
    prompts: [
      {
        name: 'Hiring Process',
        prompt: 'Explain the key employment laws I need to be aware of when hiring new staff.',
      },
      {
        name: 'Termination',
        prompt: 'What are the legal requirements for terminating an employee?',
      },
      {
        name: 'Remote Work',
        prompt: 'What legal considerations should I keep in mind for remote work policies?',
      },
    ],
  },
  {
    category: 'IP Protection',
    prompts: [
      { name: 'Trademark', prompt: 'How can I protect my brand name and logo?' },
      { name: 'Copyright', prompt: 'What steps should I take to copyright my software?' },
      { name: 'Patent', prompt: 'Explain the process of patenting a new invention.' },
    ],
  },
];

// Quick command buttons for enhanced functionality
const quickCommands = [
  'Scan all contracts from last quarter',
  'Check compliance for all clients',
  'Find cases needing attention',
  'Generate strategic insights',
  'Review recent case law',
  'Analyze contract risks',
];

const AIAssistant: React.FC = () => {
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<ExtendedChatMessage[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [recognition, setRecognition] = useState<any>(null);
  const [voiceStatus, setVoiceStatus] = useState<'ready' | 'listening' | 'error'>('ready');
  const [detectedLanguage, setDetectedLanguage] = useState<string>('auto');
  const [isEnterpriseMode, setIsEnterpriseMode] = useState(false);
  const [agentWorkflow, setAgentWorkflow] = useState<any>(null);
  const [multiAgentResponse, setMultiAgentResponse] = useState<any>(null);
  const { currentTenant } = useContext(TenantContext);
  const userInfo = getUserInfo();
  const firmId = userInfo?.organizationId || currentTenant?.id;
  const userId = userInfo?.id;
  const userRole = userInfo?.role;
  // Generate a sessionId if not present
  const [sessionId] = useState(() => {
    const existing = sessionStorage.getItem('aiSessionId');
    if (existing) return existing;
    const newId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('aiSessionId', newId);
    return newId;
  });

  // Whisper Voice Context for mic control in chat
  const { isSupported: voiceSupported, isListening: voiceListening, toggleListening } = useVoice();

  // Disable legacy Web Speech API; Whisper Core handles voice in VoiceContext
  useEffect(() => {
    setRecognition(null);
  }, []);

  // Listen for language detection updates from Whisper Core/normalize
  useEffect(() => {
    const onLang = (e: Event) => {
      const ce = e as CustomEvent;
      const lang = ce?.detail?.language;
      if (typeof lang === 'string' && lang.trim().length > 0) {
        setDetectedLanguage(lang);
      }
    };
    window.addEventListener('whisper-language-detected', onLang as EventListener);
    return () => window.removeEventListener('whisper-language-detected', onLang as EventListener);
  }, []);

  const toggleVoiceRecognition = () => {
    toggleListening();
  };

  const scrollToBottom = (force = false) => {
    if (!messagesEndRef.current) return;

    // Use requestAnimationFrame to ensure DOM is fully updated
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({
        behavior: force ? 'auto' : 'smooth',
        block: 'end',
      });
    });
  };

  // Track scroll position and user interaction
  useEffect(() => {
    const messagesContainer = messagesEndRef.current?.parentElement;
    if (!messagesContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainer;
      const wasNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setIsNearBottom(wasNearBottom);

      // Show button if more than 200px from bottom
      setShowScrollButton(scrollHeight - scrollTop - clientHeight > 200);

      // If user manually scrolls up, disable auto-scroll
      if (!wasNearBottom) {
        setShouldAutoScroll(false);
      }
    };

    const handleWheel = () => {
      // Re-enable auto-scroll when user scrolls to bottom
      if (isNearBottom) {
        setShouldAutoScroll(true);
      }
    };

    messagesContainer.addEventListener('scroll', handleScroll);
    messagesContainer.addEventListener('wheel', handleWheel);

    return () => {
      messagesContainer.removeEventListener('scroll', handleScroll);
      messagesContainer.removeEventListener('wheel', handleWheel);
    };
  }, [isNearBottom]);

  // Smart scroll behavior for new messages
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    const isAIMessage = lastMessage?.role === 'assistant';

    // Auto-scroll if:
    // 1. User hasn't disabled auto-scroll
    // 2. Message is from AI
    // 3. User is already near bottom
    if (shouldAutoScroll || isAIMessage || isNearBottom) {
      scrollToBottom(isAIMessage); // Force scroll for AI messages
    }
  }, [messages, isNearBottom, shouldAutoScroll]);

  // Global event listener for voice-triggered AI submissions
  useEffect(() => {
    const handleVoiceAISubmission = (event: CustomEvent) => {
      const { query } = event.detail;
      console.log('🎤 AIAssistant: Voice-triggered submission received:', query);

      if (query && query.trim()) {
        // Set the input value
        setInput(query);

        // Trigger submission after a short delay to ensure input is set
        setTimeout(() => {
          console.log('🎤 AIAssistant: Triggering voice-submitted query:', query);
          handleSendMessage(query);
        }, 100);
      }
    };

    // Listen for voice-triggered AI submissions
    window.addEventListener('voice-ai-submit', handleVoiceAISubmission as EventListener);

    return () => {
      window.removeEventListener('voice-ai-submit', handleVoiceAISubmission as EventListener);
    };
  }, []);

  // Enhanced message sending with enterprise features
  const handleSendMessage = async (message: string) => {
    if (!message.trim()) return;
    const userMessage: ExtendedChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: message,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    
    try {
      // Use enterprise Omniscient Agent service
      const data = await sendMessageToEnhancedAI(
        message,
        sessionId || '',
        userId || '',
        firmId || '',
        {
          current_page: window.location.pathname,
          user_role: userRole,
          enterprise_mode: true
        }
      );
      
      if (data.success) {
        const aiMessage: ExtendedChatMessage = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: data.response || data.consolidated_response || 'No response received',
          timestamp: new Date().toISOString(),
          metadata: {
            agents_used: data.agents_used,
            confidence_score: data.confidence_score,
            execution_time: data.execution_time,
            workflow_type: data.workflow_type,
            enterprise_features: data.enterprise_features
          },
          actions: data.action_buttons || []
        };
        setMessages(prev => [...prev, aiMessage]);
        // Handle workflow responses
        if (data.workflow_type) {
          setAgentWorkflow(data);
        }
      } else {
        throw new Error(data.error || 'Enterprise AI system error');
      }
    } catch (error: any) {
      console.error('Enterprise AI error:', error);
      const errorMessage: ExtendedChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: error.message || 'I apologize, but the enterprise AI system is temporarily unavailable. Please try again.',
        timestamp: new Date().toISOString(),
        error: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Enhanced voice command handling
  const handleVoiceCommand = async (voiceInput: string) => {
    try {
      const data = await sendMessageToEnhancedAI(
        voiceInput,
        sessionId || '',
        userId || '',
        firmId || '',
        {
          voice_activated: true,
          enterprise_mode: true
        }
      );
      
      if (data.success) {
        // Display voice command result
        const voiceMessage: ExtendedChatMessage = {
          id: Date.now().toString(),
          role: 'assistant',
          content: data.consolidated_response || data.response || 'Voice command processed',
          timestamp: new Date().toISOString(),
          metadata: {
            voice_processed: true,
            workflow_executed: data.enterprise_features?.workflow_orchestration,
            agents_used: data.agents_used
          },
          actions: data.action_buttons || []
        };
        setMessages(prev => [...prev, voiceMessage]);
      }
    } catch (error: any) {
      console.error('Voice command error:', error);
      const errorMessage: ExtendedChatMessage = {
        id: Date.now().toString(),
        role: 'assistant',
        content: error.message || 'Voice command processing failed. Please try again.',
        timestamp: new Date().toISOString(),
        error: true
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  const handleActionClick = async (action: ActionButton) => {
    try {
      await omniscientAgentService.handleAction(action);
      console.log('🎤 AIAssistant: Action handled successfully:', action.label);
    } catch (error) {
      console.error('🎤 AIAssistant: Error handling action:', error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(input);
    }
  };

  const handleCloseError = () => {
    setError(null);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxWidth: '800px',
        margin: '0 auto',
        padding: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'var(--border-primary)',
          bgcolor: 'var(--bg-elevated)',
        }}
      >
        {/* Messages Area */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            position: 'relative', // Add this for sticky positioning
            bgcolor: 'var(--bg-app)',
          }}
        >
          {messages.map((message, index) => {
            // Check if message has enhanced data
            const enhancedData = (message as any).omniscientData;
            
            if (message.role === 'assistant' && enhancedData) {
              // Use enhanced message component for AI messages with Omniscient data
              return (
                <EnhancedMessage
                  key={index}
                  content={message.content}
                  isAI={true}
                  timestamp={enhancedData.timestamp}
                  actions={enhancedData.actions}
                  agentUsed={enhancedData.agentUsed}
                  confidence={enhancedData.confidence}
                  onActionClick={handleActionClick}
                />
              );
            } else {
              // Use regular message display for user messages or basic AI messages
              return (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                    gap: 1,
                    mb: 2,
                  }}
                >
                  {message.role === 'assistant' && (
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <SmartToyIcon />
                    </Avatar>
                  )}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      maxWidth: '70%',
                      bgcolor:
                        message.role === 'user'
                          ? 'primary.main'
                          : alpha(theme.palette.primary.main, 0.1),
                      color: message.role === 'user' ? 'primary.contrastText' : 'text.primary',
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body1">{message.content}</Typography>
                  </Paper>
                  {message.role === 'user' && <Avatar sx={{ bgcolor: 'secondary.main' }}>U</Avatar>}
                </Box>
              );
            }
          })}
          {showScrollButton && (
            <Box
              sx={{
                position: 'sticky',
                bottom: 16,
                alignSelf: 'center',
                zIndex: 2,
              }}
            >
              <IconButton
                onClick={() => scrollToBottom(true)}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  boxShadow: 2,
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                }}
              >
                <ArrowDownwardIcon />
              </IconButton>
            </Box>
          )}
          <div ref={messagesEndRef} />
        </Box>

        {/* Input Area */}
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid',
            borderColor: 'var(--border-primary)',
            bgcolor: 'var(--bg-elevated)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              width: '100%',
            }}
          >
              <Chip
                label={`Language: ${detectedLanguage || 'auto'}`}
                size="small"
                variant="outlined"
                sx={{ mr: 0.5 }}
              />
            <TextField
              fullWidth
              multiline
              maxRows={4}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your legal question here..."
              disabled={isLoading}
              data-testid="ai-assistant-input"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'var(--bg-app)',
                  color: 'var(--text-primary)',
                  '& fieldset': {
                    borderColor: (theme) => theme.palette.mode === 'dark' ? '#404040' : 'inherit',
                  },
                  '&:hover fieldset': {
                    borderColor: (theme) => theme.palette.mode === 'dark' ? '#525252' : 'inherit',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: (theme) => theme.palette.mode === 'dark' ? '#F59E0B' : 'inherit',
                  },
                },
              }}
            />
            
            {/* Voice Activation Button */}
            <Button
              onClick={toggleVoiceRecognition}
              disabled={!voiceSupported || isLoading}
              variant="contained"
              size="small"
              sx={{
                minWidth: 'auto',
                px: 2,
                py: 1,
                bgcolor: voiceListening ? '#ff4444' : 'primary.main',
                color: 'white',
                borderRadius: 2,
                '&:hover': {
                  bgcolor: voiceListening ? '#cc3333' : 'primary.dark',
                },
                '&:disabled': {
                  bgcolor: 'grey.400',
                  color: 'grey.600',
                },
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: '0.875rem',
                textTransform: 'none',
                animation: voiceListening ? 'pulse 1.5s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.7 },
                  '100%': { opacity: 1 },
                },
              }}
            >
              {voiceListening ? <MicOffIcon sx={{ fontSize: 16 }} /> : <MicIcon sx={{ fontSize: 16 }} />}
              {voiceListening ? 'Listening…' : 'Whisper'}
            </Button>
            
            {/* Voice Status Indicator */}
            {voiceStatus !== 'ready' && (
              <Chip
                label={voiceStatus === 'listening' ? 'Listening...' : 'Voice Error'}
                size="small"
                color={voiceStatus === 'listening' ? 'primary' : 'error'}
                sx={{
                  fontSize: '0.7rem',
                  height: '24px',
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            )}
            
            <IconButton
              color="primary"
              onClick={() => handleSendMessage(input)}
              disabled={isLoading || !input.trim()}
              data-testid="ai-send-button"
              sx={{
                width: 48,
                height: 48,
                bgcolor: (theme) => theme.palette.mode === 'dark' ? '#F59E0B' : 'primary.main',
                color: (theme) => theme.palette.mode === 'dark' ? '#171717' : 'primary.contrastText',
                '&:hover': {
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? '#D97706' : 'primary.dark',
                },
              }}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
            </IconButton>
          </Box>
          
          {/* Quick Command Buttons */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Quick Commands:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {quickCommands.map((command, index) => (
                <Chip
                  key={index}
                  label={command}
                  size="small"
                  onClick={() => {
                    setInput(command);
                    handleSendMessage(command);
                  }}
                  sx={{
                    bgcolor: 'transparent',
                    color: 'primary.main',
                    border: '1px solid',
                    borderColor: 'primary.main',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'primary.main',
                      color: 'white',
                    },
                    fontSize: '0.75rem',
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AIAssistant;
